/**
 * Human-readable previews for media (image) chat messages.
 *
 * Shared between the push-notification body builder
 * (`infrastructure/lambda/notify-message-push`) and the frontend
 * conversation-list preview (`frontend/src/utils/message-reply.ts`) so both
 * surfaces say "Sent an image" / "Sent 3 images" instead of leaking the
 * `<!--ascend-images:[…]-->` body marker or the server's `[IMAGE]` placeholder.
 */

const IMAGES_MARKER_PREFIX = '<!--ascend-images:'
const MARKER_END = '-->'

export type ImagesBodyParse = {
  /** True if the string starts with an images marker (even a truncated one). */
  hasImagesMarker: boolean
  /**
   * Number of EXTRA image URLs in the marker (the primary image lives in the
   * schema's attachmentUrl). `null` when the marker is truncated (e.g. the
   * length-capped stored `lastMessagePreview`) and the count is unknowable.
   */
  extraImageCount: number | null
  /** Body text with the marker removed (caption / trailing text). */
  text: string
}

export function parseImagesBodyMarker(body: string | null | undefined): ImagesBodyParse {
  const trimmed = (body ?? '').trim()
  if (!trimmed.startsWith(IMAGES_MARKER_PREFIX)) {
    return { hasImagesMarker: false, extraImageCount: 0, text: trimmed }
  }
  const end = trimmed.indexOf(MARKER_END, IMAGES_MARKER_PREFIX.length)
  if (end < 0) {
    // Truncated marker — the closing --> was sliced off by a preview cap.
    return { hasImagesMarker: true, extraImageCount: null, text: '' }
  }
  const raw = trimmed.slice(IMAGES_MARKER_PREFIX.length, end)
  let extraImageCount = 0
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      extraImageCount = parsed.filter(
        (u): u is string => typeof u === 'string' && Boolean(u.trim()),
      ).length
    }
  } catch {
    /* malformed marker — treat as zero extra images */
  }
  let text = trimmed.slice(end + MARKER_END.length)
  if (text.startsWith('\n')) text = text.slice(1)
  return { hasImagesMarker: true, extraImageCount, text: text.trim() }
}

/**
 * "Sent an image" / "Sent 3 images" / "Sent images" (count unknown), with an
 * optional sender prefix for group-chat list rows ("Olivia sent an image").
 */
export function formatImagesSentLabel(
  totalImages: number | null,
  senderFirstName?: string,
): string {
  const subject = senderFirstName?.trim() ? `${senderFirstName.trim()} sent` : 'Sent'
  if (totalImages === null) return `${subject} images`
  if (totalImages > 1) return `${subject} ${totalImages} images`
  return `${subject} an image`
}

/** Friendly label for a media message with an empty body, by attachment type. */
export function formatAttachmentTypeLabel(
  attachmentType: string,
  senderFirstName?: string,
): string {
  const subject = senderFirstName?.trim() ? `${senderFirstName.trim()} sent` : 'Sent'
  switch (attachmentType) {
    case 'IMAGE':
      return `${subject} an image`
    case 'GIF':
      return `${subject} a GIF`
    case 'VIDEO':
      return `${subject} a video`
    case 'VOICE':
      return `${subject} a voice message`
    default:
      return `${subject} an attachment`
  }
}

const SENT_LABEL_RE = /^Sent (an image|a GIF|a video|a voice message|an attachment|images|\d+ images)\b/

/**
 * Prefix a "Sent an image"-style preview with a sender's name for group-chat
 * rows ("Olivia sent an image"). Non-media previews pass through unchanged.
 */
export function addSenderToMediaPreview(preview: string, senderName: string): string {
  const name = senderName.trim()
  if (!name || !SENT_LABEL_RE.test(preview)) return preview
  return `${name} sent ${preview.slice('Sent '.length)}`
}

/**
 * Full preview for a message that may carry image attachments: strips the
 * images marker and emits the label (plus trailing caption when present).
 * Returns `null` when the body has no marker and there's nothing to label —
 * callers fall through to their normal text handling.
 */
export function formatMediaMessagePreview(opts: {
  body: string | null | undefined
  attachmentType?: string | null
  senderFirstName?: string
}): string | null {
  const { hasImagesMarker, extraImageCount, text } = parseImagesBodyMarker(opts.body)
  if (hasImagesMarker) {
    // The primary image lives in the schema's attachmentUrl → +1.
    const total = extraImageCount === null ? null : extraImageCount + 1
    const label = formatImagesSentLabel(total, opts.senderFirstName)
    return text ? `${label} · ${text}` : label
  }
  if (!text && opts.attachmentType) {
    return formatAttachmentTypeLabel(opts.attachmentType, opts.senderFirstName)
  }
  return null
}
